import type { PaymentMethod } from '@/types/checkout';
import type { StripeApplePayRequest, StripePaymentParams } from '@/types/payment';
import type { BillingDetails, PaymentMethod as StripePaymentMethod } from '@stripe/stripe-js';

export type ApplePaySessionLike = {
  begin: () => void;
  abort?: () => void;
  completeMerchantValidation: (merchantSession: unknown) => void;
  completePayment: (result: { status: number }) => void;
  onvalidatemerchant: ((event: { validationURL: string }) => void) | null;
  onpaymentauthorized: ((event: { payment: { token: unknown } }) => void) | null;
  oncancel: ((event?: unknown) => void) | null;
};

export type ApplePaySessionConstructor = {
  new (version: number, request: Record<string, unknown>): ApplePaySessionLike;
  canMakePayments: () => boolean;
  canMakePaymentsWithActiveCard?: (merchantIdentifier: string) => Promise<boolean>;
  supportsVersion?: (version: number) => boolean;
  STATUS_SUCCESS: number;
  STATUS_FAILURE: number;
};

declare global {
  interface Window {
    ApplePaySession?: ApplePaySessionConstructor;
  }
}

export const getApplePaySessionConstructor = (): ApplePaySessionConstructor | undefined =>
  typeof window === 'undefined' ? undefined : window.ApplePaySession;

export const buildStripeApplePayRequest = (
  paymentMethodData: StripePaymentMethod,
  providerSettings: PaymentMethod,
  billingDetails: BillingDetails,
  zipCode?: string
): StripeApplePayRequest => {
  const card = paymentMethodData.card;
  if (!card) {
    throw new Error('Card data is missing from payment method');
  }

  const params: StripePaymentParams = {
    paymentMethodId: paymentMethodData.id,
    paymentMethodToken: paymentMethodData.id,
    cardDetails: {
      bin: '',
      nameOnCard: billingDetails.name || '',
      lastFourDigits: card.last4,
      expirationMonth: card.exp_month.toString(),
      expirationYear: card.exp_year.toString(),
      cardBrand: card.brand,
      fingerprint: card.fingerprint || '',
      countryOfIssuance: card.country || '',
    },
    paymentMethodDetails: {
      id: paymentMethodData.id,
      object: 'payment_method',
      allow_redisplay: 'unspecified',
      billing_details: {
        address: {
          city: billingDetails.address?.city || null,
          country: billingDetails.address?.country || null,
          line1: billingDetails.address?.line1 || '',
          line2: billingDetails.address?.line2 || null,
          postal_code: billingDetails.address?.postal_code || '',
          state: billingDetails.address?.state || null,
        },
        email: billingDetails.email || '',
        name: billingDetails.name || null,
        phone: billingDetails.phone || null,
        tax_id: null,
      },
      card: {
        brand: card.brand,
        checks: {
          address_line1_check: card.checks?.address_line1_check || null,
          address_postal_code_check: card.checks?.address_postal_code_check || null,
          cvc_check: card.checks?.cvc_check || null,
        },
        country: card.country || '',
        display_brand: card.display_brand || card.brand,
        exp_month: card.exp_month,
        exp_year: card.exp_year,
        funding: card.funding,
        generated_from: card.wallet?.apple_pay || null,
        last4: card.last4,
        networks: {
          available: card.networks?.available || [card.brand],
          preferred: card.networks?.preferred || null,
        },
        regulated_status: 'unregulated',
        three_d_secure_usage: {
          supported: card.three_d_secure_usage?.supported || true,
        },
        wallet: card.wallet
          ? {
              dynamic_last4: card.wallet.dynamic_last4 || card.last4,
              apple_pay: card.wallet.apple_pay || {},
              type: 'apple_pay',
            }
          : null,
      },
      created: paymentMethodData.created,
      customer: paymentMethodData.customer,
      livemode: paymentMethodData.livemode,
      radar_options: {},
      type: 'card',
    },
    shouldSaveCard: false,
    fraudDetection: {
      forterTokenCookie: '',
    },
  };

  return {
    paymentProvider: 'stripe',
    paymentMethodId: providerSettings.id,
    paymentMethodDisplayName: 'Apple Pay',
    receipt_email: paymentMethodData.billing_details?.email || '',
    // ACDEV-25067: only sent when the wallet ZIP resolved to a valid taxed
    // total in-sheet, so the BE can recompute the same tax for the charge.
    ...(zipCode ? { zipCode } : {}),
    params,
  };
};
